/**
 * Requirement submit/review lifecycle — follows the backend state machine
 * exactly and offers only the transitions it allows:
 *
 *   IDENTIFIED/PREPARING        → Record submission     → SUBMITTED
 *   RETURNED_FOR_REVISION /
 *   REJECTED / RESUBMITTED      → Record resubmission   → RESUBMITTED
 *   SUBMITTED / UNDER_REVIEW /
 *   RESUBMITTED                 → Record review decision (APPROVED /
 *                                 APPROVED_AS_NOTED / REJECTED /
 *                                 RETURNED_FOR_REVISION)
 *
 * Dates are observed facts (never future); negative review outcomes require
 * a written reason the submitter can act on; an UNKNOWN review duration
 * stays visibly unknown.
 */
import { useEffect, useState } from "react";
import { format, parseISO } from "date-fns";
import { toast } from "sonner";
import {
  requirementsApi,
  classifyWorkflowError,
} from "../../services/intelligenceService";
import { hasCapability } from "../../lib/capabilities";
import { useAuth } from "../../App";
import documentsService from "../../services/documentsService";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import { Send, Stamp, Loader2 } from "lucide-react";

const SUBMITTABLE = new Set(["IDENTIFIED", "PREPARING"]);
const RESUBMITTABLE = new Set(["RETURNED_FOR_REVISION", "REJECTED", "RESUBMITTED"]);
const REVIEWABLE = new Set(["SUBMITTED", "UNDER_REVIEW", "RESUBMITTED"]);

const today = () => format(new Date(), "yyyy-MM-dd");

const fmtDate = (value) => {
  if (!value) return null;
  try {
    return format(parseISO(value), "MMM d, yyyy");
  } catch {
    return String(value);
  }
};

const RequirementLifecyclePanel = ({ requirement, user, onChanged }) => {
  // The session's capability answer, read from context rather than
  // threaded as a prop: `user` already arrives from the parent, and a
  // second prop carrying the SAME authority question invites the two to
  // be passed from different places. Null-safe so rendering this panel
  // outside the provider offers nothing rather than throwing.
  const { capabilities } = useAuth() || {};
  const status = (requirement?.status || "").toUpperCase();
  const canSubmit = hasCapability(capabilities, "requirements.submit");
  const canReview = hasCapability(capabilities, "requirements.review");

  const [submitOpen, setSubmitOpen] = useState(false);
  const [reviewOpen, setReviewOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  const [submitForm, setSubmitForm] = useState({ submittedDate: today(), docIds: [], note: "" });
  const [reviewForm, setReviewForm] = useState({
    decision: "APPROVED",
    returnedDate: today(),
    conditions: "",
    note: "",
  });

  const [projectDocs, setProjectDocs] = useState(null);
  useEffect(() => {
    if (!submitOpen || projectDocs !== null || !requirement?.projectId) return;
    documentsService
      .getAll(requirement.projectId)
      .then((docs) => setProjectDocs(Array.isArray(docs) ? docs : docs?.documents || []))
      .catch(() => setProjectDocs([]));
  }, [submitOpen, projectDocs, requirement]);

  if (!requirement) return null;

  const doSubmit = async () => {
    setBusy(true);
    try {
      const body = { note: submitForm.note.trim() || undefined };
      if (submitForm.submittedDate) body.submittedDate = submitForm.submittedDate;
      if (submitForm.docIds.length) body.submittalDocumentIds = submitForm.docIds;
      const result = await requirementsApi.submit(requirement.requirementId, body);
      toast.success(result.message || `Recorded: ${result.status}.`);
      setSubmitOpen(false);
      onChanged?.();
    } catch (err) {
      toast.error(classifyWorkflowError(err).message);
    } finally {
      setBusy(false);
    }
  };

  const doReview = async () => {
    const negative = ["REJECTED", "RETURNED_FOR_REVISION"].includes(reviewForm.decision);
    if (negative && reviewForm.note.trim().length < 10) {
      toast.error("A rejection or return must carry a written reason (at least 10 characters) the submitter can act on.");
      return;
    }
    setBusy(true);
    try {
      const body = {
        decision: reviewForm.decision,
        note: reviewForm.note.trim() || undefined,
      };
      if (reviewForm.returnedDate) body.returnedDate = reviewForm.returnedDate;
      if (reviewForm.decision === "APPROVED_AS_NOTED" && reviewForm.conditions.trim()) {
        body.conditions = reviewForm.conditions.trim();
      }
      const result = await requirementsApi.review(requirement.requirementId, body);
      toast.success(result.message || `Review recorded: ${result.status}.`);
      setReviewOpen(false);
      onChanged?.();
    } catch (err) {
      toast.error(classifyWorkflowError(err).message);
    } finally {
      setBusy(false);
    }
  };

  const toggleDoc = (id) =>
    setSubmitForm((f) => ({
      ...f,
      docIds: f.docIds.includes(id) ? f.docIds.filter((d) => d !== id) : [...f.docIds, id],
    }));

  const showSubmit = canSubmit && SUBMITTABLE.has(status);
  const showResubmit = canSubmit && RESUBMITTABLE.has(status);
  const showReview = canReview && REVIEWABLE.has(status);

  if (!showSubmit && !showResubmit && !showReview) return null;

  return (
    <div className="pt-2 border-t border-border space-y-2">
      <p className="text-xs font-medium text-muted-foreground">Lifecycle actions</p>
      <div className="flex flex-wrap gap-2">
        {(showSubmit || showResubmit) && (
          <Button size="sm" variant="outline" onClick={() => setSubmitOpen(true)}>
            <Send className="w-3.5 h-3.5 mr-1.5" />
            {showResubmit ? "Record resubmission" : "Record submission"}
          </Button>
        )}
        {showReview && (
          <Button size="sm" onClick={() => setReviewOpen(true)}>
            <Stamp className="w-3.5 h-3.5 mr-1.5" />
            Record review decision
          </Button>
        )}
      </div>

      {/* Submission dialog */}
      <Dialog open={submitOpen} onOpenChange={setSubmitOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>
              {showResubmit ? "Record resubmission" : "Record submission"} — {requirement.title}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-xs text-muted-foreground">
              A submission is an observed fact — the date cannot be in the future. The
              forecast response date is derived from the recorded review duration
              {requirement.reviewDays == null
                ? "; this requirement's review duration is UNKNOWN, so the forecast will stay unknown."
                : ` (${requirement.reviewDays} days).`}
            </p>
            <div>
              <Label className="text-xs">Submitted date</Label>
              <Input
                type="date"
                className="mt-1 w-44"
                max={today()}
                value={submitForm.submittedDate}
                onChange={(e) => setSubmitForm((f) => ({ ...f, submittedDate: e.target.value }))}
              />
            </div>
            <div>
              <Label className="text-xs">Submittal documents (evidence on this project)</Label>
              {projectDocs === null ? (
                <p className="text-xs text-muted-foreground mt-1">Loading documents…</p>
              ) : projectDocs.length === 0 ? (
                <p className="text-xs text-muted-foreground mt-1">
                  No documents on this project yet — upload the submittal to the Documents
                  tab first to attach it as evidence.
                </p>
              ) : (
                <div className="mt-1 max-h-36 overflow-y-auto space-y-1 rounded-lg border border-border p-2">
                  {projectDocs.slice(0, 30).map((doc) => (
                    <label key={doc.id} className="flex items-center gap-2 text-xs cursor-pointer">
                      <input
                        type="checkbox"
                        checked={submitForm.docIds.includes(doc.id)}
                        onChange={() => toggleDoc(doc.id)}
                      />
                      <span className="truncate">{doc.name || doc.filename || doc.id}</span>
                    </label>
                  ))}
                </div>
              )}
            </div>
            <div>
              <Label className="text-xs">Note (optional)</Label>
              <Textarea
                className="mt-1"
                rows={2}
                value={submitForm.note}
                onChange={(e) => setSubmitForm((f) => ({ ...f, note: e.target.value }))}
              />
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" size="sm" onClick={() => setSubmitOpen(false)}>
                Cancel
              </Button>
              <Button size="sm" onClick={doSubmit} disabled={busy}>
                {busy && <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" />}
                Record
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Review dialog */}
      <Dialog open={reviewOpen} onOpenChange={setReviewOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Record review decision — {requirement.title}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-xs text-muted-foreground">
              Submitted {fmtDate(requirement.submittedDate) || "—"}. A review decision
              changes what dependent work is allowed to do; it requires explicit
              confirmation and, for negative outcomes, a written reason.
            </p>
            <div>
              <Label className="text-xs">Decision</Label>
              <Select
                value={reviewForm.decision}
                onValueChange={(v) => setReviewForm((f) => ({ ...f, decision: v }))}
              >
                <SelectTrigger className="mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="APPROVED">Approved</SelectItem>
                  <SelectItem value="APPROVED_AS_NOTED">Approved as noted</SelectItem>
                  <SelectItem value="RETURNED_FOR_REVISION">Returned for revision</SelectItem>
                  <SelectItem value="REJECTED">Rejected</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Response date</Label>
              <Input
                type="date"
                className="mt-1 w-44"
                max={today()}
                value={reviewForm.returnedDate}
                onChange={(e) => setReviewForm((f) => ({ ...f, returnedDate: e.target.value }))}
              />
            </div>
            {reviewForm.decision === "APPROVED_AS_NOTED" && (
              <div>
                <Label className="text-xs">Conditions attached to the approval</Label>
                <Textarea
                  className="mt-1"
                  rows={2}
                  value={reviewForm.conditions}
                  onChange={(e) => setReviewForm((f) => ({ ...f, conditions: e.target.value }))}
                />
              </div>
            )}
            <div>
              <Label className="text-xs">
                Reason / note
                {["REJECTED", "RETURNED_FOR_REVISION"].includes(reviewForm.decision)
                  ? " (required — the submitter cannot cure an unexplained rejection)"
                  : " (optional)"}
              </Label>
              <Textarea
                className="mt-1"
                rows={3}
                value={reviewForm.note}
                onChange={(e) => setReviewForm((f) => ({ ...f, note: e.target.value }))}
              />
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" size="sm" onClick={() => setReviewOpen(false)}>
                Cancel
              </Button>
              <Button size="sm" onClick={doReview} disabled={busy}>
                {busy && <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" />}
                Confirm decision
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default RequirementLifecyclePanel;
